// Ported one-to-one from @thd-olt-component-react/active-orders' src/utils/{order-status,
// fulfillment,general,tracking-utils,analytics}.ts (OOP-4059) — see FulfillmentDetailsDrawer.tsx's
// doc comment for why this is a local port rather than a package import.
//
// One deliberate substitution: the source repo formats dates/times via `luxon`. Pulling in a new
// date library for two format calls isn't worth the added dependency, so formatDate/formatTime
// below are native-Date equivalents producing the same displayed output (long/short month+day,
// "h:mma"/"ha" time), following this repo's own src/utils/date.ts convention of native
// Date + Intl rather than luxon.
import { FULFILLMENT_STATUSES, FULFILLMENT_TYPES, STATUS_CODES, UNAVAILABLE_IMAGE_URL } from './constants';

function formatDate(date: string, format: 'MMMM d' | 'MMM d' = 'MMMM d'): string {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', {
    month: format === 'MMM d' ? 'short' : 'long',
    day: 'numeric',
    timeZone: 'UTC'
  });
}

function formatTime(time: string, shortFormat = false): string {
  if (!time) return '';
  const parsed = new Date(time);
  if (Number.isNaN(parsed.getTime())) return '';
  const minutes = parsed.getUTCMinutes();
  const useHourOnly = shortFormat && minutes === 0;
  return parsed
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      ...(useHourOnly ? {} : { minute: '2-digit' }),
      timeZone: 'UTC'
    })
    .toLowerCase()
    .replace(' ', '');
}

function formatDisplayDate(date?: string) {
  return { long: date ? formatDate(date) : '', short: date ? formatDate(date, 'MMM d') : '' };
}

function formatDisplayTimeWindow(startTime?: string, endTime?: string) {
  if (!startTime || !endTime) {
    return { long: '', short: '' };
  }
  return {
    long: `from ${formatTime(startTime)} to ${formatTime(endTime)}`,
    short: `${formatTime(startTime, true)}—${formatTime(endTime, true)}`
  };
}

function getBODFSReservationWindow(fulfillmentGroup: any) {
  const delivery = fulfillmentGroup?.bodfsDeliveryAndHandling?.bodfsDeliveries?.[0];
  return { start: delivery?.reservationWindowStartTime, end: delivery?.reservationWindowEndTime };
}

function getApplianceDeliveryDate(fulfillmentGroup: any) {
  const applianceDelivery = fulfillmentGroup?.shippingAndHandling?.applianceDeliveries?.[0];
  return applianceDelivery?.currentDeliveryDate || applianceDelivery?.originalDeliveryDate;
}

function orderStatus(lineItems: any[]) {
  const activeItem = lineItems?.find((item) => item?.statusDescription !== FULFILLMENT_STATUSES.CANCELED);
  return activeItem ? activeItem?.statusDescription : lineItems?.[0]?.statusDescription;
}

function getDeliveryTrackerStatus(fulfillmentGroup: any) {
  const deliveryTrackerData = fulfillmentGroup?.deliveryTrackerData;
  const trackerStatus = deliveryTrackerData?.status;
  if (!trackerStatus) {
    return null;
  }

  const itemStatus = orderStatus(fulfillmentGroup?.lineItems);
  const deliveredDate = fulfillmentGroup?.lineItems?.[0]?.deliveredDateArray?.[0];
  const estimatedDeliveryWindowDate = deliveryTrackerData?.estimatedDeliveryWindow?.date;
  const deliveryWindow = deliveryTrackerData?.deliveryWindow;
  const estimatedPickupDate = deliveryTrackerData?.pickUpDetails?.estimatedPickupDate;

  const trackerStatusMap: {
    [key: string]: {
      statusText: string;
      deliveryStatusText: string;
      date?: { long: string; short: string };
      timeWindow?: { long: string; short: string };
    };
  } = {
    ARRIVED: { statusText: 'Arrived', deliveryStatusText: 'Arrived' },
    AWAITING_TRUCKLOAD: { statusText: 'Awaiting Truckload', deliveryStatusText: 'Awaiting Truckload' },
    IN_TRANSIT: { statusText: 'In Transit', deliveryStatusText: 'In Transit' },
    ON_THE_WAY: { statusText: 'On the Way', deliveryStatusText: 'On the Way' },
    ON_VEHICLE_FOR_DELIVERY: { statusText: 'On Vehicle for Delivery', deliveryStatusText: 'On Vehicle for Delivery' },
    MISSED_WITH_ATTEMPT: { statusText: 'Delivery Missed', deliveryStatusText: 'Delivery Missed' },
    MISSED_WITHOUT_ATTEMPT: {
      statusText: 'Delivery Missed without Attempt',
      deliveryStatusText: 'Delivery Missed without Attempt'
    },
    MISSED_WITHOUT_ENROUTE: {
      statusText: 'Delivery Missed without Enroute',
      deliveryStatusText: 'Delivery Missed without Enroute'
    },
    UNDELIVERABLE: { statusText: 'Undeliverable', deliveryStatusText: 'Undeliverable' },
    DELIVERED_WITH_EXCEPTION: { statusText: 'Delivered with Exception', deliveryStatusText: 'Delivered with Exception' },
    OUT_FOR_DELIVERY: {
      statusText: 'Arriving',
      date: formatDisplayDate(estimatedDeliveryWindowDate),
      deliveryStatusText: 'Out For Delivery'
    },
    SHIPPED: {
      statusText: 'Arriving',
      date: formatDisplayDate(estimatedDeliveryWindowDate),
      deliveryStatusText: `Arriving  By ${formatDate(estimatedDeliveryWindowDate)}`
    },
    SCHEDULED: { statusText: 'Scheduled', deliveryStatusText: 'Scheduled' },
    READY_FOR_PICKUP: {
      statusText: 'Ready for Pickup',
      date: formatDisplayDate(estimatedPickupDate),
      deliveryStatusText: `Ready for Pickup ${formatDate(estimatedPickupDate)}`
    },
    PICKED_UP: { statusText: 'Picked Up', deliveryStatusText: 'Picked Up' },
    DELAYED: { statusText: 'Delayed', deliveryStatusText: 'Delayed' }
  };

  if (trackerStatus === 'DELIVERED') {
    if (itemStatus === FULFILLMENT_STATUSES.DELIVERED) {
      return {
        statusText: 'Delivered',
        date: formatDisplayDate(deliveredDate),
        deliveryStatusText: `Delivered ${formatDate(deliveredDate, 'MMM d')}`
      };
    }
    return {
      statusText: 'Delivered',
      date: formatDisplayDate(estimatedDeliveryWindowDate),
      deliveryStatusText: `Delivered ${formatDate(estimatedDeliveryWindowDate)}`
    };
  }

  if (trackerStatus === 'ORDER_RECEIVED') {
    if (deliveryWindow?.date) {
      return {
        statusText: 'Arriving',
        date: formatDisplayDate(deliveryWindow?.date),
        timeWindow: formatDisplayTimeWindow(deliveryWindow?.startTime, deliveryWindow?.endTime),
        deliveryStatusText: `Arriving By ${formatDate(deliveryWindow?.date)}`
      };
    }
    const fulfillmentDate = fulfillmentGroup?.fulfillmentDate;
    return {
      statusText: 'Order Received',
      date: formatDisplayDate(fulfillmentDate),
      deliveryStatusText: `Order Received ${formatDate(fulfillmentDate)}`
    };
  }

  return trackerStatusMap[trackerStatus] || null;
}

/** Ported from order-status.ts's getOrderStatus — drives FulfillmentDetailsDrawer's header title. */
export function getOrderStatus(fulfillmentGroup: any) {
  const fulfillmentType = fulfillmentGroup?.fulfillmentType;
  const statusDescription = fulfillmentGroup?.lineItems?.[0]?.statusDescription;

  const defaultStatus = {
    statusText: '',
    date: { long: '', short: '' },
    timeWindow: { long: '', short: '' },
    deliveryStatusText: ''
  };

  if (!fulfillmentType || !statusDescription) {
    return defaultStatus;
  }

  if (fulfillmentGroup?.deliveryTrackerData?.status) {
    const trackerStatus = getDeliveryTrackerStatus(fulfillmentGroup);
    if (trackerStatus) {
      return trackerStatus;
    }
  }

  const shipToHomeMap = () => {
    const shipping = fulfillmentGroup?.shippingAndHandling;
    const shipmentDate = shipping?.shipments?.shipment?.[0]?.estimatedDeliveryDate;
    const estDate = shipping?.estEndDeliveryDate;
    return {
      [FULFILLMENT_STATUSES.BEING_PROCESSED]: {
        statusText: 'Estimated Arrival',
        date: formatDisplayDate(estDate),
        deliveryStatusText: `Arriving By ${formatDate(estDate)}`
      },
      [FULFILLMENT_STATUSES.SHIPPED]: {
        statusText: 'Arriving',
        date: formatDisplayDate(shipmentDate),
        deliveryStatusText: `Arriving By ${formatDate(shipmentDate)}`
      },
      [FULFILLMENT_STATUSES.OUT_FOR_DELIVERY]: {
        statusText: 'Arriving',
        date: formatDisplayDate(shipmentDate),
        deliveryStatusText: 'Out For Delivery'
      }
    };
  };

  const bossMap = () => {
    const shipping = fulfillmentGroup?.shippingAndHandling;
    const estDate = shipping?.estEndDeliveryDate;
    const shipmentDate = shipping?.shipments?.shipment?.[0]?.estimatedDeliveryDate;
    const pickupDate = fulfillmentGroup?.lineItems?.[0]?.pickupDate;
    return {
      [FULFILLMENT_STATUSES.BEING_PROCESSED]: {
        statusText: 'Ready for Pickup',
        date: formatDisplayDate(estDate),
        deliveryStatusText: `Pickup by ${formatDate(estDate)}`
      },
      [FULFILLMENT_STATUSES.ON_ITS_WAY_TO_STORE]: {
        statusText: 'Ready for Pickup',
        date: formatDisplayDate(shipmentDate),
        deliveryStatusText: `Pickup by ${formatDate(shipmentDate)}`
      },
      [FULFILLMENT_STATUSES.READY_FOR_PICKUP]: {
        statusText: 'Available until',
        date: formatDisplayDate(pickupDate),
        deliveryStatusText: `Ready for Pickup ${formatDate(pickupDate)}`
      }
    };
  };

  const bopisMap = () => {
    const fulfillmentDate = fulfillmentGroup?.fulfillmentDate;
    const pickupDate = fulfillmentGroup?.lineItems?.[0]?.pickupDate;
    return {
      [FULFILLMENT_STATUSES.BEING_PROCESSED]: {
        statusText: 'Order Received',
        date: formatDisplayDate(fulfillmentDate),
        deliveryStatusText: `Order Received ${formatDate(fulfillmentDate)}`
      },
      [FULFILLMENT_STATUSES.READY_FOR_PICKUP]: {
        statusText: 'Available Until',
        date: formatDisplayDate(pickupDate),
        deliveryStatusText: `Ready for Pickup ${formatDate(pickupDate)}`
      }
    };
  };

  const bodfsMap = () => {
    const { start, end } = getBODFSReservationWindow(fulfillmentGroup);
    return {
      [FULFILLMENT_STATUSES.BEING_PROCESSED]: {
        statusText: 'Order Received',
        date: formatDisplayDate(fulfillmentGroup?.fulfillmentDate),
        deliveryStatusText: `Order Received ${formatDate(fulfillmentGroup?.fulfillmentDate)}`
      },
      [FULFILLMENT_STATUSES.ON_SCHEDULE]: {
        statusText: 'Estimated Arrival',
        date: formatDisplayDate(start),
        timeWindow: formatDisplayTimeWindow(start, end),
        deliveryStatusText: `Arriving By ${formatDate(start)}`
      },
      [FULFILLMENT_STATUSES.SHIPPED]: {
        statusText: 'Arriving',
        date: formatDisplayDate(start),
        deliveryStatusText: `Arriving By ${formatDate(start)}`
      },
      [FULFILLMENT_STATUSES.OUT_FOR_DELIVERY]: {
        statusText: 'Arriving',
        date: formatDisplayDate(start),
        deliveryStatusText: 'Out For Delivery'
      }
    };
  };

  const applianceMap = () => {
    const estimatedDeliveryDate = getApplianceDeliveryDate(fulfillmentGroup);
    return {
      [FULFILLMENT_STATUSES.BEING_PROCESSED]: {
        statusText: 'Estimated Arrival',
        date: formatDisplayDate(estimatedDeliveryDate),
        deliveryStatusText: `Arriving By ${formatDate(estimatedDeliveryDate)}`
      },
      [FULFILLMENT_STATUSES.OUT_FOR_DELIVERY]: {
        statusText: 'Estimated Arrival',
        date: formatDisplayDate(estimatedDeliveryDate),
        deliveryStatusText: `Arriving By ${formatDate(estimatedDeliveryDate)}`
      }
    };
  };

  const miscMap = () => ({ statusText: 'Miscellaneous', deliveryStatusText: 'Miscellaneous' });

  const fulfillmentTypeMap: { [key: string]: any } = {
    [FULFILLMENT_TYPES.SHIP_TO_HOME]: shipToHomeMap(),
    [FULFILLMENT_TYPES.BUY_ONLINE_SHIP_TO_STORE]: bossMap(),
    [FULFILLMENT_TYPES.BUY_ONLINE_PICKUP_IN_STORE]: bopisMap(),
    [FULFILLMENT_TYPES.WILL_CALL_FULFILLMENT]: bopisMap(),
    [FULFILLMENT_TYPES.BUY_ONLINE_DELIVER_FROM_STORE]: bodfsMap(),
    [FULFILLMENT_TYPES.APPLIANCE]: applianceMap(),
    [FULFILLMENT_TYPES.MISC]: miscMap()
  };

  const fulfillmentTypeStatusMap = fulfillmentTypeMap?.[fulfillmentType];
  return (
    fulfillmentTypeStatusMap?.[statusDescription]
    ?? (fulfillmentType === FULFILLMENT_TYPES.MISC ? fulfillmentTypeStatusMap : defaultStatus)
  );
}

/** Ported from utils/fulfillment.ts. */
export function isCurbsidePickupEligible(fulfillmentGroup: any) {
  if (!fulfillmentGroup) return false;

  const fulfillmentType = fulfillmentGroup?.fulfillmentType;
  const lineItemStatus = fulfillmentGroup?.lineItems?.[0]?.statusCode;
  const isCurbsideAvailable = fulfillmentGroup?.storePickup?.isCurbsidePickupAvailable;
  const isInLocker = fulfillmentGroup?.storePickup?.IsInLocker;

  const eligibleFulfillmentTypes = [FULFILLMENT_TYPES.BUY_ONLINE_SHIP_TO_STORE, FULFILLMENT_TYPES.BUY_ONLINE_PICKUP_IN_STORE];
  const eligibleStatuses: number[] = [STATUS_CODES.PREPARING_FOR_PICKUP, STATUS_CODES.READY_FOR_PICKUP];

  const isEligibleFulfillment = eligibleFulfillmentTypes.includes(fulfillmentType);
  const isEligibleStatus = eligibleStatuses.includes(lineItemStatus);

  return Boolean(isCurbsideAvailable && isEligibleFulfillment && isEligibleStatus && !isInLocker);
}

/** Ported from utils/fulfillment.ts. */
export function isLockerPickupEligible(fulfillmentGroup: any, isMobile: boolean, isConsumerApp: boolean) {
  const { fulfillmentType, lineItems, storePickup } = fulfillmentGroup || {};
  const lineItemStatus = lineItems?.[0]?.statusDescription;

  return Boolean(
    (isMobile || isConsumerApp)
      && (fulfillmentType === FULFILLMENT_TYPES.BUY_ONLINE_PICKUP_IN_STORE
        || fulfillmentType === FULFILLMENT_TYPES.WILL_CALL_FULFILLMENT)
      && lineItemStatus === FULFILLMENT_STATUSES.READY_FOR_PICKUP
      && storePickup?.IsInLocker
  );
}

/** Ported from utils/general.ts. */
export function generateItemImageUrl(lineItem: any): string {
  if (lineItem?.imageUrl) {
    return lineItem.imageUrl;
  }
  if (lineItem?.giftCard?.thdImageUrl) {
    return lineItem.giftCard.thdImageUrl;
  }
  return UNAVAILABLE_IMAGE_URL;
}

/** Ported from utils/general.ts. */
export function formatPhoneNumber(phoneNumber: string): string {
  if (phoneNumber.length === 10) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
  }
  if (phoneNumber.length === 11) {
    return `${phoneNumber.slice(0, 1)}-${phoneNumber.slice(1, 4)}-${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7)}`;
  }
  return phoneNumber;
}

/** Ported from utils/general.ts. */
export function formatPrice(value: number): string {
  if (value == null) return '';
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Ported from utils/general.ts (only the fields FulfillmentDetailsActions actually passes). */
export function buildQueryString({
  orderNumber,
  orderNumbers,
  orderOrigin,
  salesDate,
  transactionId,
  registerNumber,
  storeNumber
}: {
  orderNumber?: string;
  orderNumbers?: string[];
  orderOrigin?: string;
  salesDate?: string;
  transactionId?: string;
  registerNumber?: string;
  storeNumber?: string;
}): string {
  const formattedOrderOrigin = orderOrigin?.replace('#', '%23');
  const orderNo = orderNumbers?.[0] ?? orderNumber;

  const paramMap = {
    orderNumber: orderNo,
    salesDate,
    storeNumber,
    transactionId,
    registerNumber,
    orderOrigin: formattedOrderOrigin?.trim()
  };

  const params = new URLSearchParams();
  Object.entries(paramMap).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });

  return `?${params.toString()}`;
}

/** Ported from utils/analytics.ts. */
export function pushEventInfo(analyticsEvent: Record<string, unknown>): void {
  if (!analyticsEvent || typeof window === 'undefined') {
    return;
  }
  try {
    const digitalData = (window as any).digitalData;
    if (digitalData) {
      digitalData.event = digitalData.event || [];
      digitalData.event.push(analyticsEvent);
    }
  } catch {
    // Matches the source's own defensive no-throw behavior — a failed analytics push should
    // never break the curbside-pickup action itself.
  }
}

export interface ProgressBarStep {
  icon: unknown;
  title: string;
  subtitle?: string;
}

/** Ported from utils/tracking-utils.ts's getProgressBarData (icon lookups moved to the caller). */
export function getProgressStepTitles(fulfillmentType: string): { title: string; subtitle?: string }[] {
  switch (fulfillmentType) {
    case FULFILLMENT_TYPES.SHIP_TO_HOME:
      return [
        { title: 'Processed' },
        { title: 'Shipped' },
        { title: 'Out For Delivery' },
        { title: 'Delivered' }
      ];
    case FULFILLMENT_TYPES.BUY_ONLINE_PICKUP_IN_STORE:
    case FULFILLMENT_TYPES.WILL_CALL_FULFILLMENT:
      return [{ title: 'Processed' }, { title: 'Ready for Pickup' }, { title: 'Picked Up' }];
    case FULFILLMENT_TYPES.BUY_ONLINE_SHIP_TO_STORE:
      return [
        { title: 'Processed' },
        { title: 'Shipped' },
        { title: 'Ready for Pickup' },
        { title: 'Picked Up' }
      ];
    case FULFILLMENT_TYPES.BUY_ONLINE_DELIVER_FROM_STORE:
      return [
        { title: 'Processed' },
        { title: 'Shipped' },
        { title: 'Out for Delivery' },
        { title: 'Delivered' }
      ];
    case FULFILLMENT_TYPES.APPLIANCE:
      return [
        { title: 'Processed' },
        { title: 'Delivery Window Scheduled' },
        { title: 'Out for Delivery' },
        { title: 'Delivered' }
      ];
    default:
      return [];
  }
}

/** Ported from utils/tracking-utils.ts's getActiveSteps. */
export function getActiveSteps(statusCode: number): number {
  const stepMapping: { [key: number]: number } = {
    100: 0,
    101: 0,
    102: 2,
    103: 2,
    104: 2,
    105: 3,
    106: 0,
    108: 2,
    109: 2,
    110: 2,
    130: 1,
    131: 1,
    132: 2,
    200: 3
  };
  return stepMapping[statusCode] ?? 0;
}

/**
 * Local replacement for @delivery/tracking's TrackingStepper (see FulfillmentDeliveryTracker.tsx's
 * doc comment) — maps a deliveryTrackerData `status` string to a step index over a fixed 4-stage
 * delivery pipeline, mirroring getActiveSteps' status-code mapping but keyed by the string
 * statuses TrackingStepper itself accepted (see the sibling repo's Status enum).
 */
export function getDeliveryTrackerActiveStep(status: string): number {
  const stepMapping: Record<string, number> = {
    ORDER_RECEIVED: 0,
    AWAITING_TRUCKLOAD: 0,
    SCHEDULED: 1,
    SHIPPED: 2,
    OUT_FOR_DELIVERY: 2,
    ON_THE_WAY: 2,
    ON_VEHICLE_FOR_DELIVERY: 2,
    ARRIVED: 2,
    IN_TRANSIT: 2,
    DELIVERED: 3,
    PICKED_UP: 3,
    READY_FOR_PICKUP: 1
  };
  return stepMapping[status] ?? 0;
}
